import { isNullOrUndefined}        from 'rewire-common';
import { observable }              from 'rewire-core';
import {
  IGrid,
  IRow,
  IRowData,
  IColumn,
  ICell,
  IGridOptions,
  createGrid,
  gridDefaultVariableKeybinds
}                                  from 'rewire-grid';

export function getDefaultDataEntryGridKeybinds(defaultRowData: IRowData) {
  return ({
    'Ctrl+Insert': (evt: React.KeyboardEvent<any>, cell: ICell): IRow | undefined => {
      if (cell.editing) return;
      const dataEntryRow      = getDataEntryRow(cell.grid);
      const lastRowIsSelected = cell.grid.selectedRows.some((row: IRow) => !isNullOrUndefined(dataEntryRow) && row.id === dataEntryRow!.id);
      if (lastRowIsSelected) {
        return;
      }
      const newRowData = { ...defaultRowData, data: {...defaultRowData.data} };
      const newRow     = cell.grid.rowKeybindPermissions.insertRow && cell.grid.insertRowAtSelection(newRowData) || undefined;
      evt.stopPropagation();
      evt.preventDefault();
      return newRow;
    },
    'Ctrl+D': (evt: React.KeyboardEvent<any>, cell: ICell): IRow[] | undefined => {
      const dataEntryRow      = getDataEntryRow(cell.grid);
      const lastRowIsSelected = cell.grid.selectedRows.some((row: IRow) => !isNullOrUndefined(dataEntryRow) && row.id === dataEntryRow!.id);
      if (lastRowIsSelected) {
        evt.stopPropagation();
        evt.preventDefault();
        return;
      }
      const newRows = gridDefaultVariableKeybinds['Ctrl+D'](evt, cell) as IRow[] | undefined;
      if (!newRows) {
        return;
      }
      return newRows;
    },
    'Ctrl+Delete': (evt: React.KeyboardEvent<any>, cell: ICell) => {
      const dataEntryRow      = getDataEntryRow(cell.grid);
      const lastRowIsSelected = cell.grid.selectedRows.some((row: IRow) => !isNullOrUndefined(dataEntryRow) && row.id === dataEntryRow!.id);
      if (lastRowIsSelected) {
        evt.stopPropagation();
        evt.preventDefault();
        return;
      }
      gridDefaultVariableKeybinds['Ctrl+Delete'](evt, cell);
    },
  });
}

// function getDataRows(grid: IGrid): IRow[] {
//   // excludes the blank data entry row in the grid
//   const rows = grid.rows.slice();

//   if (rows.length > 0) {
//     rows.splice(rows.length - 1, 1);
//   }
//   return rows;
// }

function getDataEntryRow(grid: IGrid): IRow | undefined {
  if (grid.rows.length <= 0) {
    return undefined;
  }

  return grid.rows[grid.rows.length - 1];
}

interface IDataEntryGridModelState {
  grid: IGrid;
}

class DataEntryGridModel {
  _defaultRowData: IRowData;
  completionColumns: IColumn[];
  onAddNewDataEntryRow?: (newDataRow: IRow) => void;
  state: IDataEntryGridModelState;

  constructor(rows: any[], columns: IColumn[], options?: IGridOptions, completionColumnNames?: string[], defaultRowData?: object, onAddNewDataEntryRow?: (newDataRow: IRow) => void) {
    this._defaultRowData   = defaultRowData || {};
    this.completionColumns = completionColumnNames && columns.filter((column: IColumn) => completionColumnNames.includes(column.name)) || [];
    this.completionColumns.forEach((column: IColumn) => {
      column.onValueChange = this.compositeColumnOnValueChange(column.onValueChange);
    });
    this.onAddNewDataEntryRow = onAddNewDataEntryRow;

    const gridOptions: IGridOptions = options || {} as IGridOptions;
    const originalIsRowCompleteFn   = gridOptions.isRowCompleteFn;
    gridOptions.isRowCompleteFn     = (rowData: IRowData): boolean => {
      const row      = rowData as IRow;
      const complete = originalIsRowCompleteFn ? originalIsRowCompleteFn(row) : true;
      return complete && ((row.position <= row.grid.rows.length - 1 && row.id !== row.grid.rows[row.grid.rows.length - 1].id) || this.completionColumns.every((column: IColumn) => !isNullOrUndefined((row as IRow).cells[column.name].value)));
    };

    const defaultDataEntryKeybinds = getDefaultDataEntryGridKeybinds(this.defaultRowData);
    if (gridOptions.variableKeybinds) {
      if (!gridOptions.variableKeybinds['Ctrl+Insert']) gridOptions.variableKeybinds['Ctrl+Insert'] = defaultDataEntryKeybinds['Ctrl+Insert'];
      if (!gridOptions.variableKeybinds['Ctrl+D'])      gridOptions.variableKeybinds['Ctrl+D']      = defaultDataEntryKeybinds['Ctrl+D'];
      if (!gridOptions.variableKeybinds['Ctrl+Delete']) gridOptions.variableKeybinds['Ctrl+Delete'] = defaultDataEntryKeybinds['Ctrl+Delete'];
    } else {
      gridOptions.variableKeybinds = defaultDataEntryKeybinds;
    }

    // add data entry row
    rows.push(this.defaultRowData);

    this.state = observable({
      grid: createGrid(rows, columns, gridOptions),
    });
  }

  get grid(): IGrid {
    return this.state.grid;
  }

  get defaultRowData(): IRowData {
    return { ...this._defaultRowData, data: {...this._defaultRowData.data} };
  }

  compositeColumnOnValueChange(onValueChange?: (cell: ICell, value: any) => void): (cell: ICell, value: any) => void {
    if (onValueChange) {
      return ((cell: ICell, value: any) => {
        onValueChange(cell, value);
        this.autoAddNewDataEntryRow(cell);
      });
    } else {
      return ((cell: ICell, _value: any) => { this.autoAddNewDataEntryRow(cell); });
    }
  }

  autoAddNewDataEntryRow(cell: ICell) {
    const row = cell.row;
    if (row.position < cell.grid.rows.length - 1) {
      return;
    }

    if (this.completionColumns.every((column: IColumn) => row.cells[column.name] && !isNullOrUndefined(row.cells[column.name].value))) {
      this.addNewDataEntryRow();
    }
  }

  addNewDataEntryRow() {
    const prevDataEntryRow = getDataEntryRow(this.grid);
    const newRowData       = this.defaultRowData;

    this.grid.addRow(newRowData);

    if (prevDataEntryRow) {
      // validate previous data entry row if it exists, as it is now a considered a completed row
      prevDataEntryRow.validate();
      if (this.onAddNewDataEntryRow) {
        this.onAddNewDataEntryRow(prevDataEntryRow);
      }
    }
  }
}

export default function createDataEntryGrid(rows: any[], columns: IColumn[], options?: IGridOptions, completionColumnNames?: string[], defaultRowData?: object, onAddNewDataEntryRow?: (newDataRow: IRow) => void): IGrid {
  const dataEntryGridModel = new DataEntryGridModel(rows, columns, options, completionColumnNames, defaultRowData, onAddNewDataEntryRow);
  return dataEntryGridModel.grid;
}